import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSelector } from 'react-redux';

import { useToken } from '../../../common/helper';
import { getPermissions } from '../../../common/helper/getPermissions';
import { CloseOutlined } from '@ant-design/icons';
import {
  CreateNewShipment,
  SuggestShipmentReferenceNumber,
  uploadFile,
} from '../../../apis/shipment';
import { AddSettingsLocation } from '../../../apis/settings';
import {
  getLocationDDL,
  getTeamsDDLOFFeature,
} from '../../../common/utils/dropDown';
import LocationAutoCompleteShipment from '../../Modals/LocationAutoCompleteShipment';
import SaveLocationModal from '../../Modals/SaveLocationModal';
import { GoogleMapsProvider } from '../../../components/GoogleMapsProvider';
import { CustomButton, CustomModal } from '../../../components';
import PolygonFenceMap from '../../../components/PolygonFenceMap';
import usePolygonEditSession from '../../../hooks/usePolygonEditSession';
import TeamDropdownSelect from '../../../components/TeamDropdownSelect';
import EditLocation from '../../../assets/images/edit_location.svg';
import { GeoFenceIcon, UndoIcon } from '@/assets/icons/icons';
import type {
  ErrorLike,
  LatLng,
  LocationData,
  MenuItemData,
  SingleShipmentData,
} from '../../../apis/types';
import SettingsProductDrawerForm from '../../Settings/SettingsProductDrawerForm';
import { usePaginatedProductDropdown } from '../../../hooks/usePaginatedProductDropdown';
import { useUnAuthorize } from '../../../hooks';
import styles from './styles.module.css';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';
import { ProductMultiSelectField, ProductQuantityRows } from '../components';
import { applyProductQuantityDelta } from '../shellShipmentHelpers';
import {
  MAX_QUANTITY_MESSAGE,
  MIN_QUANTITY_MESSAGE,
} from '@/utils/formatQuantity';
import useUserDateTimeFormat from '../../../hooks/useUserDateTimeFormat';

dayjs.extend(utc);

type DrawerView = 'shipment' | 'product_form';

interface Team {
  id: string;
  name: string;
  color?: string;
  permissions?: {
    is_create?: boolean;
    is_update?: boolean;
  };
  disabled?: boolean;
}

interface SelectedProduct {
  id: string;
  quantity: number;
}

/** `id` is set for saved LocationV2 rows; `placeName` for fresh Google places. */
interface LocationOption {
  id?: string;
  name?: string;
  placeName?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  polygon?: LatLng[];
  zip_code?: string;
  country?: string;
}

interface MenuState {
  items?: MenuItemData[] | null;
}

interface RootState {
  menu: MenuState;
}

interface CreateShellShipmentDrawerProps {
  onClose: () => void;
  onSuccess?: (shipment: SingleShipmentData) => void;
}

interface ShellFormValues {
  reference_number: string;
  link_code?: string;
  preferred_ship_date?: Dayjs;
  products?: string[];
  teams?: string[];
}

const buildLocationDisplay = (loc: LocationOption | null): string => {
  if (!loc) return '';
  if (loc.id && loc.address && !loc.placeName) {
    return `${loc.name ?? ''} · ${loc.address}`;
  }
  return loc.placeName || loc.name || '';
};

const uploadFileKeys = new WeakMap<File, number>();
let nextUploadFileKey = 0;
const getUploadFileKey = (file: File): number => {
  let key = uploadFileKeys.get(file);
  if (key === undefined) {
    key = nextUploadFileKey++;
    uploadFileKeys.set(file, key);
  }
  return key;
};

const DEFAULT_POLYGON_MAP_CENTER: LatLng = { lat: 37.7749, lng: -122.4194 };

const hasValidLatLng = (
  point: { lat: number | null; lng: number | null } | undefined
): point is LatLng =>
  !!point && Number.isFinite(point.lat) && Number.isFinite(point.lng);

const CreateShellShipmentDrawer: React.FC<CreateShellShipmentDrawerProps> = ({
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<ShellFormValues>();
  const [productDrawerForm] = Form.useForm();
  const { token } = useToken();
  const { unauthorize } = useUnAuthorize();
  // The dropdown hook reports caught errors as `unknown`; unauthorize only reads `.status`.
  const handleProductDropdownUnauthorized = useCallback(
    (error: unknown) => unauthorize(error as ErrorLike),
    [unauthorize]
  );
  const { dateFormat } = useUserDateTimeFormat();
  const preferredShipDatePickerFormat =
    dateFormat === 'DD/MM/YYYY'
      ? 'DD-MM-YYYY hh:mm A'
      : dateFormat === 'YYYY/MM/DD'
        ? 'YYYY-MM-DD hh:mm A'
        : 'MM-DD-YYYY hh:mm A';
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [teamsSearch, setTeamsSearch] = useState('');
  const [debouncedTeamsSearch, setDebouncedTeamsSearch] = useState('');
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [drawerView, setDrawerView] = useState<DrawerView>('shipment');
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    []
  );

  const {
    products: paginatedProducts,
    getProductName,
    mergeIntoLookup,
    setSearch: setProductSearch,
    searchInputKey: productSearchInputKey,
    loading: productDropdownLoading,
    catalogTotalCount: productCatalogTotalCount,
    search: productSearch,
    handlePopupScroll: handleProductPopupScroll,
  } = usePaginatedProductDropdown({
    token: token ?? '',
    productsRefreshKey,
    unauthorize: handleProductDropdownUnauthorized,
  });

  const [fromLocation, setFromLocation] = useState<LocationOption | null>(null);
  const [toLocation, setToLocation] = useState<LocationOption | null>(null);
  const [fromDisplay, setFromDisplay] = useState<string>('');
  const [toDisplay, setToDisplay] = useState<string>('');
  const fromLocationRef = useRef<LocationOption | null>(null);
  const toLocationRef = useRef<LocationOption | null>(null);
  const fromLatLngRef = useRef<{ lat: number | null; lng: number | null }>({
    lat: null,
    lng: null,
  });
  const toLatLngRef = useRef<{ lat: number | null; lng: number | null }>({
    lat: null,
    lng: null,
  });
  // True after the user confirms a polygon edit (draw or clear) for that field;
  // blocks late GetSettingsLocationById enrichment from restoring the old fence.
  const polygonFromEditedRef = useRef(false);
  const polygonToEditedRef = useRef(false);
  // Explicit detail-hydration gate for geo-fence (not inferred from nullable lat).
  const [fromDetailHydrating, setFromDetailHydrating] = useState(false);
  const [toDetailHydrating, setToDetailHydrating] = useState(false);

  // Bookmark / save-location modal
  const [saveLocationModalOpen, setSaveLocationModalOpen] =
    useState<boolean>(false);
  const [saveLocationTarget, setSaveLocationTarget] = useState<
    'from' | 'to' | null
  >(null);

  // Attachments (uploaded after shipment is created)
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Map polygon modal
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalName, setModalName] = useState<string>('');
  const [locationName, setLocationName] = useState<string>('');
  const [polygonFrom, setPolygonFrom] = useState<LatLng[]>([]);
  const [polygonTo, setPolygonTo] = useState<LatLng[]>([]);
  const [selectedLatLng1, setSelectedLatLng1] = useState<{
    lat: number | null;
    lng: number | null;
  }>({ lat: null, lng: null });
  const [selectedLatLng2, setSelectedLatLng2] = useState<{
    lat: number | null;
    lng: number | null;
  }>({ lat: null, lng: null });
  const polygonSession = usePolygonEditSession();

  const mainMenu = useSelector((state: RootState) => state.menu.items);
  const locationsPermissions = getPermissions(
    mainMenu ?? undefined,
    'Settings',
    'Locations'
  );
  const canCreateLocation = locationsPermissions?.is_create ?? false;

  useEffect(() => {
    fromLocationRef.current = fromLocation;
  }, [fromLocation]);
  useEffect(() => {
    toLocationRef.current = toLocation;
  }, [toLocation]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedTeamsSearch(teamsSearch);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [teamsSearch]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const teamsRequest = debouncedTeamsSearch
      ? getTeamsDDLOFFeature(token, 'Shipment', debouncedTeamsSearch)
      : getTeamsDDLOFFeature(token, 'Shipment');

    teamsRequest
      .then((val) => {
        if (cancelled) return;
        const teams = (val || []) as Team[];
        setAllTeams(teams);
      })
      .catch((error) => {
        console.error('Shell drawer dropdown load failed', error);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedTeamsSearch, token]);

  useEffect(() => {
    if (!token) return;
    getLocationDDL(token, true)
      .then((val) => {
        if (val?.length) setLocations(val as LocationOption[]);
      })
      .catch((error) => {
        console.error('Shell drawer dropdown load failed', error);
      });
  }, [token]);

  const handleTeamsSearch = useCallback((val: string) => {
    setTeamsSearch(val);
  }, []);

  // Prefill reference number from the suggest endpoint; user can overwrite.
  useEffect(() => {
    if (!token) return;
    SuggestShipmentReferenceNumber(token)
      .then((res) => {
        const payload = res?.data as
          { data?: { data?: { reference_number?: string } } } | undefined;
        const candidate = payload?.data?.data?.reference_number;
        if (candidate) {
          form.setFieldsValue({ reference_number: candidate });
        }
      })
      .catch(() => {
        // Non-blocking — user can type one in.
      });
  }, [token, form]);

  const updateQuantity = (id: string, delta: number): void => {
    const { next, error } = applyProductQuantityDelta(
      selectedProducts,
      id,
      delta
    );
    if (error === 'min') {
      message.warning(MIN_QUANTITY_MESSAGE);
      return;
    }
    if (error === 'max') {
      message.warning(MAX_QUANTITY_MESSAGE);
      return;
    }
    setSelectedProducts(next);
  };

  const removeProduct = (id: string | number): void => {
    let updated = selectedProducts.filter((p) => p.id !== id);
    updated = updated.filter((p) => p.id !== 'all_products');
    setSelectedProducts(updated);
    form.setFieldsValue({
      products: updated.map((p) => p.id),
    });
  };

  const handleAddressPlaceSelect = (
    place: LocationOption & { address?: string },
    fieldName: string,
    options?: { isDetailEnrichment?: boolean }
  ): void => {
    const lat = place?.latitude != null ? Number(place.latitude) : null;
    const lng = place?.longitude != null ? Number(place.longitude) : null;
    const next: LocationOption = {
      ...place,
      latitude: typeof lat === 'number' && !Number.isNaN(lat) ? lat : undefined,
      longitude:
        typeof lng === 'number' && !Number.isNaN(lng) ? lng : undefined,
    };
    const displayName = buildLocationDisplay(next);
    if (fieldName === 'from_location') {
      const prev = fromLocationRef.current;
      // Only GetSettingsLocationById enrichment takes the soft-merge path —
      // never a fresh lightweight select of the same id with null/missing lat.
      const sameIdEnrichment =
        !!options?.isDetailEnrichment && !!prev?.id && prev.id === place.id;
      fromLatLngRef.current = { lat, lng };
      setFromDisplay(displayName);
      setSelectedLatLng1({ lat, lng });
      // Soft-merge detail hydration: keep a post-select polygon edit (draw or
      // clear). Read polygon from the location ref (updated in onModalConfirm)
      // — not polygonFrom state — because this callback may still be the
      // closure from the pre-edit select render.
      if (sameIdEnrichment) {
        const enriched = polygonFromEditedRef.current
          ? { ...next, polygon: prev?.polygon ?? [] }
          : next;
        fromLocationRef.current = enriched;
        setFromLocation(enriched);
        if (!polygonFromEditedRef.current) {
          setPolygonFrom(next.polygon ?? []);
        }
      } else {
        polygonFromEditedRef.current = false;
        fromLocationRef.current = next;
        setFromLocation(next);
        setPolygonFrom(next.polygon ?? []);
      }
    } else {
      const prev = toLocationRef.current;
      const sameIdEnrichment =
        !!options?.isDetailEnrichment && !!prev?.id && prev.id === place.id;
      toLatLngRef.current = { lat, lng };
      setToDisplay(displayName);
      setSelectedLatLng2({ lat, lng });
      if (sameIdEnrichment) {
        const enriched = polygonToEditedRef.current
          ? { ...next, polygon: prev?.polygon ?? [] }
          : next;
        toLocationRef.current = enriched;
        setToLocation(enriched);
        if (!polygonToEditedRef.current) {
          setPolygonTo(next.polygon ?? []);
        }
      } else {
        polygonToEditedRef.current = false;
        toLocationRef.current = next;
        setToLocation(next);
        setPolygonTo(next.polygon ?? []);
      }
    }
  };

  const handleAddressInputChange = (
    target: 'from' | 'to',
    value: string
  ): void => {
    const loc =
      target === 'from' ? fromLocationRef.current : toLocationRef.current;
    if (!loc?.id) {
      if (target === 'from') {
        fromLatLngRef.current = { lat: null, lng: null };
        setSelectedLatLng1({ lat: null, lng: null });
        setFromDisplay(value);
      } else {
        toLatLngRef.current = { lat: null, lng: null };
        setSelectedLatLng2({ lat: null, lng: null });
        setToDisplay(value);
      }
      return;
    }
    const expected = buildLocationDisplay(loc);
    if (value !== expected) {
      if (target === 'from') {
        fromLatLngRef.current = { lat: null, lng: null };
        setFromLocation(null);
        setFromDisplay(value);
        setSelectedLatLng1({ lat: null, lng: null });
        setFromDetailHydrating(false);
      } else {
        toLatLngRef.current = { lat: null, lng: null };
        setToLocation(null);
        setToDisplay(value);
        setSelectedLatLng2({ lat: null, lng: null });
        setToDetailHydrating(false);
      }
    }
  };

  // ---------------------------- Bookmark (save location) ----------------------------

  const handleBookmarkClick = (target: 'from' | 'to'): void => {
    const addressStr = target === 'from' ? fromDisplay : toDisplay;
    if (!addressStr) return;
    setSaveLocationTarget(target);
    setSaveLocationModalOpen(true);
  };

  const handleSaveLocation = async (newName: string): Promise<void> => {
    const target = saveLocationTarget;
    if (!target || !token) return;
    const fieldName = target === 'from' ? 'from_location' : 'to_location';
    const displayValue = target === 'from' ? fromDisplay : toDisplay;
    const locRef =
      target === 'from' ? fromLocationRef.current : toLocationRef.current;
    const addressStr = locRef?.address || displayValue;
    const latLng =
      target === 'from' ? fromLatLngRef.current : toLatLngRef.current;
    const teamIds = selectedTeams.map((t) => t.id);

    try {
      const res = await AddSettingsLocation(token, {
        name: newName,
        address: addressStr,
        latitude: latLng.lat,
        longitude: latLng.lng,
        zip_code: locRef?.zip_code ?? '',
        country: locRef?.country ?? '',
        teams: teamIds,
        polygon: target === 'from' ? polygonFrom : polygonTo,
        contacts: [],
        receiving_hours: { start: null, end: null },
        location_type: null,
        id: null,
      });

      const resData = (
        res as {
          data?: {
            data?: {
              is_success?: boolean;
              data?: {
                created_locations?: LocationOption & { address?: string };
              };
            };
          };
        }
      )?.data?.data;
      if (!resData?.is_success) {
        throw new Error('API returned is_success: false');
      }

      const created = resData?.data?.created_locations;
      const newLoc: LocationOption & { address?: string } = {
        ...(created ?? {}),
        id: created?.id,
        name: created?.name ?? newName,
        address: created?.address ?? addressStr,
        latitude: latLng.lat ?? undefined,
        longitude: latLng.lng ?? undefined,
      };

      setLocations((prev) => [newLoc, ...prev]);
      getLocationDDL(token, true)
        .then((refreshed) => {
          if (refreshed?.length) setLocations(refreshed as LocationOption[]);
        })
        .catch(() => {
          /* silent — optimistic entry already in list */
        });

      handleAddressPlaceSelect(newLoc, fieldName);
      setSaveLocationModalOpen(false);
      message.success('Location saved');
    } catch (err) {
      message.error('Failed to save location. Please try again.');
      throw err;
    }
  };

  // ---------------------------- Map polygon modal ----------------------------

  const handleDetailHydrationChange = useCallback(
    (fieldName: string, phase: 'start' | 'done') => {
      const pending = phase === 'start';
      if (fieldName === 'from_location') setFromDetailHydrating(pending);
      else if (fieldName === 'to_location') setToDetailHydrating(pending);
    },
    []
  );

  const openMapModal = (name: string): void => {
    // Gate on explicit detail-request state — not nullable lat/lng (a saved
    // location may legitimately have null coords, and failed fetches settle).
    if (
      (name === 'from_location' && fromDetailHydrating) ||
      (name === 'to_location' && toDetailHydrating)
    ) {
      return;
    }
    setLocationName(name);
    setModalName('Draw Polygon');
    const committed = name === 'from_location' ? polygonFrom : polygonTo;
    polygonSession.load(committed, committed.length >= 3);
    setModalOpen(true);
  };

  const onModalConfirm = (): void => {
    if (polygonSession.isValid || polygonSession.isEmpty) {
      const nextPolygon = polygonSession.polygon;
      if (locationName === 'from_location') {
        setPolygonFrom(nextPolygon);
        polygonFromEditedRef.current = true;
        // Keep fromLocation.polygon in sync so submit fallback cannot restore
        // a cleared fence from the hydrated location record.
        setFromLocation((prev) => {
          if (!prev) return prev;
          const next = { ...prev, polygon: nextPolygon };
          fromLocationRef.current = next;
          return next;
        });
      } else {
        setPolygonTo(nextPolygon);
        polygonToEditedRef.current = true;
        setToLocation((prev) => {
          if (!prev) return prev;
          const next = { ...prev, polygon: nextPolygon };
          toLocationRef.current = next;
          return next;
        });
      }
      message.success(
        polygonSession.isEmpty ? 'Polygon removed.' : 'Polygon created'
      );
      setModalOpen(false);
    } else {
      message.error('Please close the polygon before saving.');
    }
  };

  const handleSetModalOpen = (item: boolean): void => {
    if (item === false) {
      polygonSession.discard();
      setModalOpen(false);
    }
  };

  const polygonMapCenter: LatLng = hasValidLatLng(
    locationName === 'from_location' ? selectedLatLng1 : selectedLatLng2
  )
    ? ((locationName === 'from_location'
        ? selectedLatLng1
        : selectedLatLng2) as LatLng)
    : DEFAULT_POLYGON_MAP_CENTER;

  // ---------------------------- Attachments ----------------------------

  const handleFilesSelected = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const incoming = event.target.files;
    if (incoming && incoming.length > 0) {
      setUploadFiles((current) => [...current, ...Array.from(incoming)]);
    }
    event.target.value = '';
  };

  const openFileDialog = (event?: React.MouseEvent): void => {
    event?.stopPropagation();
    fileInputRef.current?.click();
  };

  const removeUploadFile = (index: number): void => {
    setUploadFiles((current) => current.filter((_, i) => i !== index));
  };

  const handleDropZoneDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    const dropped = event.dataTransfer.files;
    if (dropped && dropped.length > 0) {
      setUploadFiles((current) => [...current, ...Array.from(dropped)]);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // ---------------------------- Submit ----------------------------

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    let values: ShellFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!token) {
      setError('You must be signed in to create a shipment.');
      return;
    }

    const teamIds = selectedTeams.map((t) => t.id);
    if (teamIds.length === 0) {
      setError(
        `Please select ${themeT('team', theme.themeType)?.toLowerCase()}s`
      );
      return;
    }

    const productsField = selectedProducts
      .filter((p) => p.id && p.id !== 'all_products' && p.quantity)
      .map((p) => ({
        product_id: p.id,
        quantity: p.quantity,
      }));

    const model: Record<string, unknown> = {
      is_shell_create: true,
      reference_number: values.reference_number,
      teams: teamIds,
      products: productsField,
      primary_team: teamIds[0],
    };
    if (values.link_code) {
      model.link_code = values.link_code;
    }
    if (values.preferred_ship_date) {
      // DatePicker gives a local-mode dayjs (browser timezone).
      // toISOString() always emits UTC, so the backend stores the correct instant.
      model.customer_preferred_pickup_date =
        values.preferred_ship_date.toISOString();
    }
    // Matches CreateShipmentDrawer submission shape:
    //   - saved location → send the full object (backend extracts the id)
    //   - new Google place → send from_address / to_address as a string
    if (fromLocation) {
      if (fromLocation.id) {
        // Existing ids: send polygon only when the user edited it (draw or
        // clear). Re-sending a hydrated fence is redundant and unsafe when
        // O/D share the same id — an unedited destination payload can
        // overwrite an origin clear on flush.
        const { polygon: _fromPoly, ...fromRest } = fromLocation;
        const fromPayload: Record<string, unknown> = { ...fromRest };
        if (polygonFromEditedRef.current) {
          fromPayload.polygon = polygonFrom;
        }
        model.from_location = fromPayload;
      } else {
        model.from_address = fromLocation.address || fromLocation.placeName;
      }
    }
    if (toLocation) {
      if (toLocation.id) {
        const { polygon: _toPoly, ...toRest } = toLocation;
        const toPayload: Record<string, unknown> = { ...toRest };
        if (polygonToEditedRef.current) {
          toPayload.polygon = polygonTo;
        }
        model.to_location = toPayload;
      } else {
        model.to_address = toLocation.address || toLocation.placeName;
      }
    }

    setSubmitting(true);
    try {
      const res = await CreateNewShipment(token, model);
      const body = res?.data as
        | {
            data?: {
              is_success?: boolean;
              data?: SingleShipmentData;
              message?: string;
            };
            message?: string;
          }
        | undefined;
      const inner = body?.data;
      if (!inner?.is_success) {
        setError(
          inner?.message || body?.message || 'Failed to create shipment.'
        );
        return;
      }
      const createdShipment = inner.data as SingleShipmentData;
      // Upload any attached files now that we have a shipment ID. Best-effort:
      // a failure surfaces a soft error but doesn't roll back the shipment.
      if (uploadFiles.length > 0 && createdShipment?.id) {
        const failures: string[] = [];
        await Promise.all(
          uploadFiles.map(async (file) => {
            try {
              const form = new FormData();
              form.append('shipment_id', createdShipment.id);
              form.append('file', file);
              await uploadFile(token, form);
            } catch {
              failures.push(file.name);
            }
          })
        );
        if (failures.length > 0) {
          message.warning(
            `Shipment created, but failed to attach: ${failures.join(', ')}`
          );
        }
      }
      message.success('Shipment created');
      onSuccess?.(createdShipment);
      onClose();
    } catch (err) {
      const msg =
        (err as { data?: { data?: { message?: string }; message?: string } })
          ?.data?.data?.message ||
        (err as { data?: { message?: string } })?.data?.message ||
        'Failed to create shipment.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------- Render helpers ----------------------------

  /** SVG bookmark suffix for the location autocomplete. Solid = saved; outline = click to save. */
  const renderBookmarkIcon = (target: 'from' | 'to'): React.ReactNode => {
    const display = target === 'from' ? fromDisplay : toDisplay;
    const loc = target === 'from' ? fromLocation : toLocation;
    if (!canCreateLocation || !display) return undefined;
    if (loc?.id && !loc?.placeName) {
      return (
        <svg
          width='11'
          height='14'
          viewBox='0 0 14 18'
          fill='var(--text-primary)'
          className={styles.bookmarkIconStatic}
        >
          <path d='M1 1h12v16l-6-4-6 4V1z' />
        </svg>
      );
    }
    return (
      <svg
        width='11'
        height='14'
        viewBox='0 0 14 18'
        fill='none'
        stroke='#888'
        strokeWidth='1.5'
        className={styles.bookmarkIconAction}
        onClick={() => handleBookmarkClick(target)}
      >
        <path d='M1 1h12v16l-6-4-6 4V1z' strokeLinejoin='round' />
      </svg>
    );
  };

  return (
    <div className={styles.container}>
      <div className={drawerView === 'shipment' ? undefined : styles.hidden}>
        <h2 className={styles.title}>Create new shipment</h2>

        <Form
          className={styles.shellForm}
          form={form}
          layout='vertical'
          requiredMark
          colon={false}
          initialValues={{ preferred_ship_date: dayjs() }}
        >
          <Row gutter={[8, 16]}>
            <Col span={12} className={styles.fieldGridFull}>
              <Form.Item
                label={`${themeT('referenceId', theme.themeType)}`}
                name='reference_number'
                rules={[
                  {
                    required: true,
                    message: `${themeT('referenceId', theme.themeType)} is required`,
                  },
                ]}
              >
                <Input placeholder='For internal reference' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <Typography.Text
                    className='link-code-create'
                    ellipsis={{ tooltip: 'Link code (optional)' }}
                  >
                    {`${themeT('linkCode', theme.themeType)} (optional)`}
                  </Typography.Text>
                }
                name='link_code'
                rules={[
                  {
                    required: false,
                    pattern: /^[a-zA-Z0-9]{0,255}$/,
                    message: 'Please enter up to 255 alphanumeric characters',
                  },
                ]}
              >
                <Input placeholder='Code' maxLength={255} />
              </Form.Item>
            </Col>
          </Row>
          <div className={styles.fieldGrid}>
            {/* Team */}
            <Col span={24} className={styles.fieldGridFull}>
              <Form.Item
                className='custom-input form-input-style'
                label={`${themeT('team', theme.themeType)}s`}
                name='teams'
                rules={[
                  {
                    required: true,
                    message: `Please select ${themeT('team', theme.themeType)?.toLowerCase()}s`,
                  },
                ]}
              >
                <TeamDropdownSelect
                  teamsOptions={allTeams}
                  selectedTeams={selectedTeams}
                  onChange={setSelectedTeams}
                  formState='add'
                  onSearch={handleTeamsSearch}
                  teamsSearch={teamsSearch}
                />
              </Form.Item>
            </Col>

            {/* Products */}
            <Col span={24} className={styles.fieldGridFull}>
              <ProductMultiSelectField
                token={token ?? ''}
                form={form}
                selectedProducts={selectedProducts}
                onProductsChange={setSelectedProducts}
                getProductName={getProductName}
                mergeIntoLookup={mergeIntoLookup}
                paginatedProducts={paginatedProducts}
                productCatalogTotalCount={productCatalogTotalCount}
                productDropdownLoading={productDropdownLoading}
                productSearchInputKey={productSearchInputKey}
                productSearch={productSearch}
                setProductSearch={setProductSearch}
                handlePopupScroll={handleProductPopupScroll}
                onOpenAddProduct={() => setDrawerView('product_form')}
              />
            </Col>
            {/* product quantity */}
            {selectedProducts.length > 0 && (
              <Col span={24} className={styles.fieldGridFull}>
                <ProductQuantityRows
                  selectedProducts={selectedProducts}
                  setSelectedProducts={setSelectedProducts}
                  getProductName={getProductName}
                  updateQuantity={updateQuantity}
                  removeProduct={removeProduct}
                />
              </Col>
            )}

            <Form.Item
              label='Preferred ship date'
              name='preferred_ship_date'
              className={styles.fieldGridFull}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder='Enter date & time'
                showTime={{ format: 'hh:mm A', use12Hours: true }}
                format={preferredShipDatePickerFormat}
              />
            </Form.Item>

            <div className={styles.fieldGridFull}>
              <GoogleMapsProvider>
                <div className={styles.pickupRow}>
                  <div className={styles.pickupInput}>
                    <LocationAutoCompleteShipment
                      locationList={locations as LocationData[]}
                      handleAddressPlaceSelect={(place, _field, selectOpts) =>
                        handleAddressPlaceSelect(
                          place as LocationOption & { address?: string },
                          'from_location',
                          selectOpts
                        )
                      }
                      fieldName='from_location'
                      label='Pickup & delivery'
                      width='100%'
                      className='custom-input form-input-style'
                      placeholder='Enter pick-up address/location'
                      externalValue={fromDisplay}
                      onInputChange={(val) =>
                        handleAddressInputChange('from', val)
                      }
                      onDetailHydrationChange={handleDetailHydrationChange}
                      suffixIcon={renderBookmarkIcon('from')}
                    />
                  </div>
                  <span className={styles.mapBtn}>
                    <CustomButton
                      isFormValid={!fromDisplay || fromDetailHydrating}
                      image={polygonFrom.length >= 3 ? undefined : EditLocation}
                      icon={
                        polygonFrom.length >= 3 ? (
                          <GeoFenceIcon color='#fff' />
                        ) : undefined
                      }
                      iconPosition='left'
                      border='none'
                      width='36px'
                      height='36px'
                      borderRadius='50%'
                      bgColor={polygonFrom.length >= 3 ? '#000' : '#f5f5f5'}
                      color='var(--text-primary)'
                      boxShadow='none'
                      onClick={() => openMapModal('from_location')}
                      padding='0'
                    />
                  </span>
                </div>
                <div className={styles.pickupRow}>
                  <div className={styles.pickupInput}>
                    <LocationAutoCompleteShipment
                      locationList={locations as LocationData[]}
                      handleAddressPlaceSelect={(place, _field, selectOpts) =>
                        handleAddressPlaceSelect(
                          place as LocationOption & { address?: string },
                          'to_location',
                          selectOpts
                        )
                      }
                      fieldName='to_location'
                      label=''
                      width='100%'
                      className='label-13px'
                      placeholder='Enter delivery address/location'
                      externalValue={toDisplay}
                      onInputChange={(val) =>
                        handleAddressInputChange('to', val)
                      }
                      onDetailHydrationChange={handleDetailHydrationChange}
                      suffixIcon={renderBookmarkIcon('to')}
                    />
                  </div>
                  <span className={styles.mapBtn}>
                    <CustomButton
                      isFormValid={!toDisplay || toDetailHydrating}
                      image={polygonTo.length >= 3 ? undefined : EditLocation}
                      icon={
                        polygonTo.length >= 3 ? (
                          <GeoFenceIcon color='#fff' />
                        ) : undefined
                      }
                      iconPosition='left'
                      border='none'
                      width='36px'
                      height='36px'
                      borderRadius='50%'
                      bgColor={polygonTo.length >= 3 ? '#000' : '#f5f5f5'}
                      color='var(--text-primary)'
                      boxShadow='none'
                      onClick={() => openMapModal('to_location')}
                      padding='0'
                    />
                  </span>
                </div>
              </GoogleMapsProvider>
            </div>
          </div>

          {/* Upload files — drop zone + browse button + selected-file rows */}
          <div className={styles.uploadSection}>
            <div className={styles.uploadLabel}>Upload files</div>
            <div
              className={styles.uploadDropZone}
              onClick={openFileDialog}
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleDropZoneDrop}
            >
              <p className={styles.uploadDropText}>
                Drop files here to upload...
              </p>
              <button
                type='button'
                className={styles.browseButton}
                onClick={openFileDialog}
              >
                Browse files
              </button>
            </div>
            {uploadFiles.length > 0 && (
              <div className={styles.fileList}>
                {uploadFiles.map((f, idx) => (
                  <div key={getUploadFileKey(f)} className={styles.fileRow}>
                    <div className={styles.fileMeta}>
                      <span className={styles.fileName}>{f.name}</span>
                      <span className={styles.fileSize}>
                        {formatFileSize(f.size)}
                      </span>
                    </div>
                    <button
                      type='button'
                      className={styles.fileRemove}
                      aria-label='Remove file'
                      onClick={() => removeUploadFile(idx)}
                    >
                      <CloseOutlined />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input
              type='file'
              multiple
              ref={fileInputRef}
              className={styles.hidden}
              onChange={handleFilesSelected}
            />
          </div>

          {error && <div className={styles.errorBanner}>{error}</div>}

          <div className={styles.actionsBlock}>
            <Button
              className={styles.primaryAction}
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting}
              type='primary'
            >
              Save
            </Button>
            <button
              type='button'
              className={styles.linkAction}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </Form>

        <SaveLocationModal
          open={saveLocationModalOpen}
          address={
            saveLocationTarget === 'from' ? fromDisplay || '' : toDisplay || ''
          }
          onSave={handleSaveLocation}
          onCancel={() => setSaveLocationModalOpen(false)}
        />

        <CustomModal
          modalOpen={modalOpen}
          setModalOpen={handleSetModalOpen}
          modalTitle={modalName}
          modalText=''
          modalSize={680}
          buttonText1='Cancel'
          buttonText2='Submit'
          onConfirmClick={onModalConfirm}
        >
          <GoogleMapsProvider>
            <div className='polygon-map-shell'>
              <PolygonFenceMap
                center={polygonMapCenter}
                polygon={polygonSession.polygon}
                closed={polygonSession.isClosed}
                resetTrigger={polygonSession.resetTrigger}
                onPolygonChange={polygonSession.setPolygon}
                onClosedChange={polygonSession.setIsClosed}
              />
              {polygonSession.polygon.length > 0 && (
                <button
                  type='button'
                  className='polygon-map-undo-btn'
                  onClick={polygonSession.undo}
                >
                  <UndoIcon size={12} />
                  <span>Undo</span>
                </button>
              )}
            </div>
          </GoogleMapsProvider>
        </CustomModal>
      </div>

      <div
        className={drawerView === 'product_form' ? undefined : styles.hidden}
      >
        <SettingsProductDrawerForm
          form={productDrawerForm}
          id={null}
          showBackButton
          handleCloseDrawer={() => {
            setDrawerView('shipment');
            productDrawerForm.resetFields();
          }}
          handleFetchAllProduct={() => {
            setProductsRefreshKey((prev) => prev + 1);
            setDrawerView('shipment');
            productDrawerForm.resetFields();
          }}
        />
      </div>
    </div>
  );
};

export default CreateShellShipmentDrawer;
